/**
 * GLOBAL ROUTE RESOLUTION BOUNDARY
 *
 * Wraps the entire route tree. Ensures no route renders its final content
 * until the canonical access state is fully resolved.
 *
 * This prevents:
 * - flash of wrong content during auth hydration
 * - premature redirects on hard refresh / new tab
 * - race conditions between lazy-loaded routes and auth state
 *
 * SYSTEM INVARIANT: No route decision occurs until isFullyResolved === true.
 */

import { useAccessState } from "@/hooks/useAccessState";
import { useLocation, Navigate } from "react-router";
import { getRouteRequirement } from "@/lib/auth/route-requirements";
import { getDefaultRouteForRole } from "@/lib/auth/routing";
import { AppShellLoading } from "./AppShellLoading";

interface RouteResolutionBoundaryProps {
  children: React.ReactNode;
}

export function RouteResolutionBoundary({ children }: RouteResolutionBoundaryProps) {
  const accessState = useAccessState();
  const { pathname } = useLocation();
  const requirement = getRouteRequirement(pathname);

  // ── Any route that needs auth info must wait for resolution ──
  const needsResolution =
    requirement.requireAuth ||
    requirement.publicOnly ||
    requirement.requireStableAuth;

  if (needsResolution && !accessState.isFullyResolved) {
    if (import.meta.env.DEV) {
      console.debug(
        `[RouteResolutionBoundary] Buffering "${pathname}" (${requirement.label}) — waiting for resolution`
      );
    }
    return <AppShellLoading />;
  }

  // ── Public-only routes: redirect authenticated users away ──
  if (requirement.publicOnly && accessState.isFullyResolved && accessState.isAuthenticated) {
    const target = getDefaultRouteForRole(accessState.role);
    if (import.meta.env.DEV) {
      console.debug(
        `[RouteResolutionBoundary] Redirecting authenticated user from "${pathname}" → "${target}"`
      );
    }
    return <Navigate to={target} replace />;
  }

  // ── Dev assertion: catch any code path that slips through ──
  if (import.meta.env.DEV && needsResolution && !accessState.isFullyResolved) {
    console.error(
      `[RouteResolutionBoundary] BUG: Route "${pathname}" rendered while isFullyResolved=false`
    );
  }

  return <>{children}</>;
}
